import { logger } from '@beelday/common'
import { useCallback, useEffect, useRef, useState } from 'react'

export type SendStatus = 'idle' | 'sending' | 'confirmed' | 'failed'

/** How long the answer may take to show up in the room's live state. */
export const CONFIRM_TIMEOUT_MS = 10_000

export type ConfirmedSend = {
	status: SendStatus
	/**
	 * `baseline` is the progress the send is measured from - `progress` as it
	 * stands, unless the caller is about to switch what `progress` counts.
	 */
	send: (request: () => Promise<unknown>, baseline?: number) => void
	reset: () => void
}

/**
 * Sends an answer and reports it as sent only once the room's live state
 * shows it.
 *
 * The answer endpoints reply 2xx whatever the server decided - a poll that
 * was stopped mid-send, a different poll now current, a single-answer poll
 * already answered all come back "OK" and are only logged server side. A 2xx
 * therefore proves nothing; the answer appearing in the SSE-fed store does.
 *
 * `progress` is a count that grows when the server records one of my answers
 * (my entries in the poll's answers). The send is confirmed when it exceeds
 * the value it had when the send started. It must count only what this send
 * can produce: with several questions it is the count for the question being
 * sent, or a late answer to another question would confirm this one.
 *
 * It is reported failed `CONFIRM_TIMEOUT_MS` after the send *started*, not
 * after the request completed: the client's requests retry until they
 * succeed, so offline a request may never complete, and waiting on it left
 * the button spinning for good. A late echo still counts - an answer that was
 * recorded after the timeout turns "failed" into "confirmed" - so slow venue
 * Wi-Fi never strands an answer that did arrive.
 */
export const useConfirmedSend = (progress: number): ConfirmedSend => {
	const [status, setStatus] = useState<SendStatus>('idle')
	const baseline = useRef(0)
	const mounted = useRef(true)

	useEffect(
		() => () => {
			mounted.current = false
		},
		[]
	)

	// The echo can arrive before the POST resolves, and after the send was
	// already given up on; either way it confirms.
	useEffect(() => {
		if (
			(status === 'sending' || status === 'failed') &&
			progress > baseline.current
		) {
			setStatus('confirmed')
		}
	}, [progress, status])

	useEffect(() => {
		if (status !== 'sending') {
			return
		}
		const timeout = setTimeout(() => {
			setStatus(current => (current === 'sending' ? 'failed' : current))
		}, CONFIRM_TIMEOUT_MS)
		return () => clearTimeout(timeout)
	}, [status])

	const send = useCallback(
		(request: () => Promise<unknown>, from: number = progress) => {
			if (status === 'sending') {
				return
			}
			baseline.current = from
			setStatus('sending')
			request().catch(error => {
				logger.error('Failed to send an answer', error)
				if (mounted.current) {
					setStatus(current => (current === 'sending' ? 'failed' : current))
				}
			})
		},
		[progress, status]
	)

	const reset = useCallback(() => setStatus('idle'), [])

	return { status, send, reset }
}

export default useConfirmedSend
